import React, { useEffect, useState } from 'react';
import { getCars, updateApprovalStatusCar } from '../services/api';
import { FaEye } from 'react-icons/fa';
import ReactPaginate from 'react-paginate';
import { useNavigate } from 'react-router-dom';
import { useLocation } from 'react-router-dom';

const ManageVehicles = () => {
  const [sort, setSort] = useState('createdAt');
  const [order, setOrder] = useState('desc');
  const [cars, setCars] = useState([]);
  const [allCars, setAllCars] = useState([]);
  const [expandedRows, setExpandedRows] = useState({});
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(0);
  const [pageCount, setPageCount] = useState(0);
  const [filters, setFilters] = useState({
      approvalStatus: '',
      transmission: '',
      isOnHold: '',
  });
  const [editModeCarId, setEditModeCarId] = useState(null);
  const [editedStatus, setEditedStatus] = useState('');
  const [selectedCarIds, setSelectedCarIds] = useState([]);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [bulkAction, setBulkAction] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const [selectedCar, setSelectedCar] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const fetchCars = async () => {
      try {
        const data = await getCars();
        setAllCars(Array.isArray(data) ? data : data.documents || []);
      } catch (error) {
        console.error('Failed to fetch vehicles:', error);
      }
    };
    fetchCars();
  }, []);

  useEffect(() => {
      const applyFilters = () => {
        let filtered = [...allCars];

        // Filter by approval status, dual role, onboarding
        if (filters.approvalStatus)
          filtered = filtered.filter((o) => o.approvalStatus === filters.approvalStatus);

        if (filters.transmission)
          filtered = filtered.filter((o) => o.transmission === filters.transmission);

        if (filters.isOnHold)
          filtered = filtered.filter((o) => String(o.isOnHold) === filters.isOnHold);

        if (search.trim()) {
          const lowerSearch = search.toLowerCase();
          filtered = filtered.filter((o) =>
            o.schoolOwnerId?.toLowerCase().includes(lowerSearch) ||
            o.carMake?.toLowerCase().includes(lowerSearch) ||
            o.carId?.toLowerCase().includes(lowerSearch) ||
            o.insuranceValidityDates?.toLowerCase().includes(lowerSearch) ||
            o.makeYear?.toLowerCase().includes(lowerSearch) ||
            o.model?.toLowerCase().includes(lowerSearch) ||
            o.pollutionValidityDates?.toLowerCase().includes(lowerSearch) ||
            o.priceWithLicense?.toLowerCase().includes(lowerSearch) ||
            o.priceWithoutLicense?.toLowerCase().includes(lowerSearch) ||
            o.registeredNumber?.toLowerCase().includes(lowerSearch)
          );
        }

        // Sort
        filtered.sort((a, b) => {
          const dateA = a[sort]?.seconds || 0;
          const dateB = b[sort]?.seconds || 0;
          return order === 'asc' ? dateA - dateB : dateB - dateA;
        });

        const start = currentPage * 10;
        const paginated = filtered.slice(start, start + 10);

        setCars(paginated);
        setPageCount(Math.ceil(filtered.length / 10));
      };

      applyFilters();
    }, [allCars, filters, sort, order, currentPage, search]);

  useEffect(() => {
      const params = new URLSearchParams(location.search);
      const idFromUrl = params.get('carId');
      if (idFromUrl) {
        setSearch(idFromUrl);
      }
    }, [location.search]);

    const handlePageChange = ({ selected }) => {
      setCurrentPage(selected);
    };

    const handleFilterChange = (e) => {
      setFilters({ ...filters, [e.target.name]: e.target.value });
      setCurrentPage(0);
    };

    const handleSortChange = (field) => {
      setSort(field);
      setOrder(order === 'asc' ? 'desc' : 'asc');
      setCurrentPage(0);
    };

    const toggleRow = (id) => {
      setExpandedRows((prev) => ({ ...prev, [id]: !prev[id] }));
    };

  return (
    <div className="p-6 text-gray-800 dark:text-gray-100">
      <h2 className="text-2xl font-poppins text-driveeasy-blue dark:text-driveeasy-blue2 mb-4">Manage Vehicles</h2>

      {/* Search */}
      <div className="mb-4 flex flex-wrap items-center gap-4">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by model, number, make..."
           className="p-2 border rounded-md w-72 bg-white dark:bg-neutral-700 dark:border-neutral-600 dark:text-gray-200"
        />
        <select name="approvalStatus" value={filters.approvalStatus} onChange={handleFilterChange} className="p-2 border rounded-md bg-white dark:bg-neutral-700 dark:border-neutral-600 dark:text-gray-200">
          <option value="">All Statuses</option>
          <option value="PENDING">Pending</option>
          <option value="APPROVED">Approved</option>
          <option value="REJECTED">Rejected</option>
        </select>
        <select name="transmission" value={filters.transmission} onChange={handleFilterChange} className="p-2 border rounded-md bg-white dark:bg-neutral-700 dark:border-neutral-600 dark:text-gray-200">
          <option value="">Select</option>
          <option value="Automatic">Automatic</option>
          <option value="Manual">Manual</option>
        </select>
        <select name="isOnHold" value={filters.isOnHold} onChange={handleFilterChange} className="p-2 border rounded-md bg-white dark:bg-neutral-700 dark:border-neutral-600 dark:text-gray-200">
          <option value="">Select</option>
          <option value="true">On Hold</option>
          <option value="false">Not on Hold</option>
        </select>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full bg-white dark:bg-neutral-800 shadow rounded-lg text-sm border border-gray-100 dark:border-neutral-800">
          <thead>
            <tr className="bg-driveeasy-blue text-white dark:bg-neutral-700 dark:text-gray-200">
              <th className="p-3 text-left">Car ID</th>
              <th className="p-3 text-left">School ID</th>
              <th className="p-3 text-left">Car Make</th>
              <th className="p-3 text-left">Model</th>
              <th className="p-3 text-left">Make Year</th>
              <th className="p-3 text-left">Transmission</th>
              <th className="p-3 text-left">Reg. Number</th>
              <th className="p-3 text-left">Price W</th>
              <th className="p-3 text-left">Price W/o</th>
              <th className="p-3 text-left">Approval</th>
              <th
                  className="p-3 text-left cursor-pointer"
                  onClick={() => handleSortChange('createdAt')}
                >
                  Added On {sort === 'createdAt' && (order === 'asc' ? '↑' : '↓')}
                </th>
              <th className="p-3 text-left">Actions</th>
              <th className="p-3 text-left">
                <input
                  type="checkbox"
                  checked={selectedCarIds.length === cars.length && cars.length > 0}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedCarIds(cars.map((o) => o.carId));
                    } else {
                      setSelectedCarIds([]);
                    }
                  }}
                />
              </th>
            </tr>
          </thead>
          <tbody>
            {cars.length > 0 ? cars.map(car => (
              <React.Fragment key={car.carId}>
                <tr className="border-b border-gray-200 dark:border-neutral-700 hover:bg-gray-50 dark:hover:bg-neutral-700/70 text-black dark:text-gray-300">
                <td
                    className="p-3 text-driveeasy-blue hover:underline cursor-pointer"
                    onClick={() => {
                      setSelectedCar(car);
                      setProfileModalOpen(true);
                    }}
                  >
                    {car.carId}
                  </td>
                  <td
                    className="p-3 text-driveeasy-blue underline cursor-pointer"
                    onClick={() => navigate(`/school-owners?schoolOwnerId=${car.schoolOwnerId}`)}
                  >
                    {car.schoolOwnerId || 'N/A'}
                  </td>
                  <td className="p-3">{car.carMake || 'N/A'}</td>
                  <td className="p-3">{car.model || 'N/A'}</td>
                  <td className="p-3">{car.makeYear || 'N/A'}</td>
                  <td className="p-3">{car.transmission || 'N/A'}</td>
                  <td className="p-3">{car.registeredNumber || 'N/A'}</td>
                  <td className="p-3">{car.priceWithLicense || 'N/A'}</td>
                  <td className="p-3">{car.priceWithoutLicense || 'N/A'}</td>
                  <td className="p-3">
                     {editModeCarId === car.carId ? (
                       <select
                         value={editedStatus}
                         onChange={(e) => setEditedStatus(e.target.value)}
                         className="border p-1 rounded"
                       >
                         {['PENDING', 'APPROVED', 'REJECTED'].map((status) => (
                           <option key={status} value={status}>{status}</option>
                         ))}
                       </select>
                     ) : (
                       <span className={`px-2 py-1 rounded ${car.approvalStatus === 'PENDING'
                         ? 'bg-orange-500' : car.approvalStatus === 'APPROVED'
                           ? 'bg-green-500' : 'bg-red-500'} text-white`}>
                         {car.approvalStatus || 'N/A'}
                       </span>
                     )}
                   </td>
                  <td className="p-3">
                    {car.createdAt?.seconds ? new Date(car.createdAt.seconds * 1000).toLocaleDateString() : 'N/A'}
                  </td>
                  <td className="p-3">
                    <button onClick={() => toggleRow(car.carId)}>
                      <FaEye className={expandedRows[car.carId] ? 'text-driveeasy-yellow' : 'text-driveeasy-blue'} />
                    </button>
                  </td>
                  <td className="p-3">
                    <input
                      type="checkbox"
                      checked={selectedCarIds.includes(car.carId)}
                      onChange={() =>
                        setSelectedCarIds((prev) =>
                          prev.includes(car.carId)
                            ? prev.filter((id) => id !== car.carId)
                            : [...prev, car.carId]
                        )
                      }
                    />
                  </td>
                </tr>
                {expandedRows[car.carId] && (
                  <tr>
                    <td colSpan="8" className="p-3 bg-gray-50 dark:bg-neutral-800 text-sm">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <h4 className="font-poppins text-driveeasy-blue">Car Details</h4>
                          <p><strong>Hold:</strong> {car.isOnHold ? 'Yes' : 'No'}</p>
                          {car.updatedAt && <p>Updated At: {car.updatedAt}</p>}
                          <p><strong>Insurance Validity:</strong> {car.insuranceValidityDates || 'N/A'}</p>
                          <p><strong>Pollution Validity:</strong> {car.pollutionValidityDates || 'N/A'}</p>
                          <p><strong>Location:</strong> {car.schoolLocation?.latitude || 'N/A'}, {car.schoolLocation?.longitude || 'N/A'}</p>
                        </div>
                        <div>
                          {car.allotedTo && <p>Alloted To: {car.allotedTo}</p>}
                          {car.assignedInstructorName && <p>Instructor Name: {car.assignedInstructorName}</p>}
                          {car.assignedInstructorId && <p>Instructor ID: {car.assignedInstructorId}</p>}
                          {car.anyOtherPricing && <p>Other Pricing: {car.anyOtherPricing?.name || 'N/A'}, {car.anyOtherPricing?.price || 'N/A'}</p>}
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            )) : (
              <tr>
                <td colSpan="8" className="text-center p-4">No vehicles found.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <ReactPaginate
        previousLabel={'Previous'}
        nextLabel={'Next'}
        pageCount={pageCount}
        onPageChange={handlePageChange}
        containerClassName={'flex justify-center mt-4 space-x-2'}
        pageClassName={'px-3 py-1 border rounded border-gray-200 dark:border-neutral-600 hover:bg-driveeasy-blue hover:text-white dark:text-gray-100'}
        activeClassName={'bg-driveeasy-blue text-white'}
        previousClassName={'px-3 py-1 border rounded border-gray-200 dark:border-neutral-700 hover:bg-driveeasy-blue hover:text-white dark:text-gray-100'}
        nextClassName={'px-3 py-1 border rounded border-gray-200 dark:border-neutral-700 hover:bg-driveeasy-blue hover:text-white dark:text-gray-100'}
      />
      {selectedCarIds.length > 0 && (
      <div className="fixed bottom-4 left-1/2 transform -translate-x-1/2 bg-white  dark:bg-neutral-800 border border-gray-300 dark:border-neutral-600 shadow-lg px-6 py-3 rounded-lg flex items-center gap-4 z-50 text-gray-700 dark:text-gray-400">
        <span className="text-gray-700 dark:text-white">{selectedCarIds.length} selected</span>
        <button
          onClick={() => {
            setBulkAction('approve');
            setShowConfirmModal(true);
          }}
          className="bg-green-500 text-white px-3 py-1 rounded"
        >
          Approve Selected
        </button>
        <button
          onClick={() => {
            setBulkAction('reject');
            setShowConfirmModal(true);
          }}
          className="bg-red-500 text-white px-3 py-1 rounded"
        >
          Reject Selected
        </button>
        <button
          onClick={() => setSelectedCarIds([])}
          className="text-gray-600 underline dark:text-white underline"
        >
          Clear
        </button>
      </div>
    )}
      {showConfirmModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-neutral-800 dark:text-gray-200 p-6 rounded shadow-lg w-96 border border-gray-200 dark:border-neutral-700">
            <h2 className="text-lg font-bold mb-4">Confirm Action</h2>
            <p className="mb-4">
              {bulkAction === 'single'
                ? `Are you sure you want to update status to "${editedStatus}"?`
                : `Are you sure you want to ${bulkAction} ${selectedCarIds.length} selected cars?`}
            </p>
            <div className="flex justify-end gap-4">
              <button onClick={() => setShowConfirmModal(false)} className="text-gray-600">Cancel</button>
              <button
                onClick={async () => {
                  setIsLoading(true);
                  try {
                    if (bulkAction === 'single') {
                      await updateApprovalStatusCar(editModeCarId, editedStatus);
                      setAllCars((prev) =>
                        prev.map((o) =>
                          o.carId === editModeCarId ? { ...o, approvalStatus: editedStatus } : o
                        )
                      );
                      setEditModeCarId(null);
                    } else {
                      const updated = await Promise.all(
                        selectedCarIds.map((carId) =>
                          updateApprovalStatusCar(carId, bulkAction === 'approve' ? 'APPROVED' : 'REJECTED')
                        )
                      );
                      setAllCars((prev) =>
                        prev.map((o) =>
                          selectedCarIds.includes(o.carId)
                            ? { ...o, approvalStatus: bulkAction === 'approve' ? 'APPROVED' : 'REJECTED' }
                            : o
                        )
                      );
                      setSelectedCarIds([]);
                    }
                    setShowConfirmModal(false);
                  } catch (error) {
                    console.error("Update failed", error);
                  } finally {
                    setIsLoading(false);
                  }
                }}
                className="bg-driveeasy-blue text-white px-4 py-2 rounded"
                disabled={isLoading}
              >
                {isLoading ? 'Processing...' : 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}
      {profileModalOpen && selectedCar && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center">
          <div className="bg-white dark:bg-neutral-800 dark:text-gray-200 p-6 rounded-lg max-w-2xl w-full max-h-[80vh] overflow-y-auto shadow-xl border border-gray-200 dark:border-neutral-700">
            <h3 className="text-xl font-bold mb-4 text-driveeasy-blue">Full Profile</h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <h4 className="font-semibold text-driveeasy-blue">Car Info</h4>
                <p><strong>UID:</strong> {selectedCar.carId}</p>
                <p><strong>Owner Name:</strong> {selectedCar.ownerName || 'N/A'}</p>
                <p><strong>Phone:</strong> {selectedCar.phoneNumber || 'N/A'}</p>
                <p><strong>Email:</strong> {selectedCar.email || 'N/A'}</p>
                <p><strong>Aadhaar:</strong> {selectedCar.aadhaarNumber || 'N/A'}</p>
                <p><strong>PAN:</strong> {selectedCar.panNumber || 'N/A'}</p>
                <p><strong>Approval Status:</strong> {selectedCar.approvalStatus}</p>
                <p><strong>Dual Role:</strong> {selectedCar.isDualRole ? 'Yes' : 'No'}</p>
                <p><strong>Onboarding Complete:</strong> {selectedCar.isOnboardingComplete ? 'Yes' : 'No'}</p>
              </div>
              <div>
                <h4 className="font-semibold text-driveeasy-blue">Address</h4>
                <p><strong>School Name:</strong> {selectedCar.schoolName || 'N/A'}</p>
                <p><strong>Address:</strong> {selectedCar.addressLine1 || 'N/A'}</p>
                <p><strong>City:</strong> {selectedCar.city || 'N/A'}</p>
                <p><strong>State:</strong> {selectedCar.state || 'N/A'}</p>
                <p><strong>Pincode:</strong> {selectedCar.pincode || 'N/A'}</p>
                <p><strong>Landmark:</strong> {selectedCar.landmark || 'N/A'}</p>
                <p><strong>Location:</strong> {selectedCar.location?.latitude || 'N/A'}, {selectedCar.location?.longitude || 'N/A'}</p>
              </div>
            </div>

            <div className="mt-4 flex justify-end">
              <button
                className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
                onClick={() => setProfileModalOpen(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageVehicles;
